<template>
  <portal>
    <div class="gallery-mobile-component">
      <div class="gallery-mobile-component__header">
        <div class="gallery-mobile-component__close" @click="closeManually">
          <img
            src="~/assets/img/svg/common/close.svg"
            alt="закрыть"
          />
        </div>
      </div>
      <!-- Overlay с индикатором загрузки пока заблокирован скролл -->
      <div v-if="isScrollLocked" class="gallery-mobile-component__loading-overlay">
        <div class="gallery-mobile-component__loading-content">
          <div class="gallery-mobile-component__loader"></div>
          <p>Загрузка изображения...</p>
        </div>
      </div>
      <div ref="imagesContainer" class="gallery-mobile-component__images" :class="{ 'scroll-locked': isScrollLocked }">
        <div
          v-for="(commonItem, index) in commonItemsSection"
          :key="`commonItem-${ index }`"
          :ref="(el) => setItemRef(el, index)"
        >
          <template v-if="commonItem.type === 'images' && index <= currentLoadingIndex">
            <suspense>
              <nuxt-img
                :src="String(commonItem.id)"
                width="640"
                fit="contain"
                :alt="`gallery-mobile-image-${commonItem.id}-index-${commonItem.id}`"
                @load="() => onItemLoad('images', index)"
              />
            </suspense>
          </template>
          <template v-if="commonItem.type === 'files' && index <= currentLoadingIndex">
            <iframe
              :src="prepareVideoUrl(String(commonItem.url))"
              allowfullscreen
              frameborder="0"
              width="100%"
              height="100%"
              allow="autoplay"
              encrypted-media
              picture-in-picture
              @load="() => onItemLoad('files', index)"
            />
          </template>
          <template v-if="commonItem.type === 'userImagesIds' && index <= currentLoadingIndex">
            <suspense>
              <div style="position:relative;">
                <nuxt-img
                  :src="String(commonItem.url)"
                  width="640"
                  fit="contain"
                  :alt="`gallery-mobile-image-${commonItem.url}-index-${commonItem.url}`"
                  @load="() => onItemLoad('userImagesIds', index)"
                />
                <div v-if="commonItem.hint" class="collection-slider-images__slide-hint">
                  {{ commonItem.hint }}
                </div>
              </div>
            </suspense>
          </template>
          <!-- Placeholder для элементов, которые еще не загружаются -->
          <template v-if="index > currentLoadingIndex">
            <div class="gallery-mobile-component__placeholder">
              <div class="gallery-mobile-component__loader"></div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </portal>
</template>

<script setup lang="ts">
import useGalleryComponent from '~/composables/useGallery';
import {computed, type ComputedRef} from 'vue';

const galleryComponent = useGalleryComponent();
const images: ComputedRef<any[]> = computed(() => {
  return galleryComponent?.galleryState?.value?.imagesIds || []
});
const files: ComputedRef<any[]> = computed(() => {
  return galleryComponent?.galleryState?.value?.videos || []
});
const userImagesIds: ComputedRef<any[]> = computed(() => {
  const _product: any = galleryComponent?.galleryState.value?.product;
  const _products: any = galleryComponent?.galleryState.value?.products;
  const userImagesIds = [...(_products?.dynamicFields?.user_images_ids || _product?.dynamicFields?.user_images_ids || [])];
  if (userImagesIds.length <= 0) {
    return []
  }

  return  userImagesIds.map((imageId: number) => {
    return {
      url: String(imageId),
      hint: "Фото от клиентов",
    }
  })
});

const commonItemsSection: ComputedRef<any[]> = computed(() => {
  return [
    ...(images.value || []).map((t: any) => {
      return {id: t, type: 'images'}
    }),
    ...(files.value || []).map((t: any) => {
      return {...t, type: 'files'}
    }),
    ...(userImagesIds.value || []).map((t: any) => {
      return {...t, type: 'userImagesIds'}
    }),
  ]
})

const close = () => {
  galleryComponent.closeGallery();
};

// Функция для самостоятельного закрытия галереи (не через popstate)
const closeManually = () => {
  galleryComponent.closeGallery();

  // При самостоятельном закрытии нужно очистить состояние истории
  // Используем nextTick чтобы дать время на реактивные изменения
  nextTick(() => {
    // Возвращаемся к предыдущему состоянию истории
    history.back();
  });
};

const refImages: any = ref(null);
const imagesContainer = ref<HTMLElement | null>(null);

// Состояние для отслеживания текущего индекса загрузки
const currentLoadingIndex = ref(0);
const loadedItems = ref<number[]>([]);

// Храним ссылки на все элементы галереи
const itemRefs = ref<Map<number, HTMLElement>>(new Map());

// Получаем индекс активного слайда из состояния
const indexActiveSlide = computed(() => galleryComponent?.galleryState?.value?.indexActiveSlide ?? 0);

// Флаг, что скролл к активному элементу уже выполнен
const hasScrolledToActive = ref(false);

// Флаг блокировки скролла
const isScrollLocked = ref(false);

// Обработчик события popstate для закрытия галереи при нажатии кнопки "назад"
const handlePopState = (event: PopStateEvent) => {
  // Если галерея открыта, закрываем её и отменяем переход назад
  if (galleryComponent.isGalleryOpen.value) {
    close();

    // Заменяем текущее состояние истории, не добавляя новую запись
    // Это отменяет переход назад, оставляя пользователя на той же странице без галереи
    nextTick(() => {
      history.replaceState(null, '', window.location.href);
    });
  }
};

// Функция для установки ref элемента
const setItemRef = (el: any, index: number) => {
  if (el) {
    itemRefs.value.set(index, el);
  }
};

// логика для формирования ссылки iframe
const prepareVideoUrl = function(url: string) {
  let videoUrl = '';
  if (url.includes('vk.com')) {
    const videoIds = url.split('-')?.[1]
    const videoOid = videoIds.split('_')?.[0]
    const videoId = videoIds.split('_')?.[1]

    videoUrl = `https://vk.com/video_ext.php?oid=-${videoOid}&id=${videoId}&autoplay=0&loop=1&volume=0`
  }
  if (url.includes('youtube')) {
    videoUrl = url
  }
  return videoUrl;
}

// Функция скролла к элементу с учетом offset от шапки
const scrollToItem = (index: number) => {
  const element = itemRefs.value.get(index);
  if (element && imagesContainer.value) {
    // Получаем позицию элемента относительно контейнера
    const elementPosition = element.offsetTop;
    const offsetFromTop = 60; // Отступ от верха (высота шапки)

    // Выполняем плавный скролл с учетом offset
    imagesContainer.value.scrollTo({
      top: elementPosition - offsetFromTop,
      behavior: 'smooth'
    });
  } else {
    console.warn(`Элемент с индексом ${index} не найден в itemRefs или контейнер не доступен`);
  }

  // Разблокируем скролл после завершения анимации скролла в любом случае
  setTimeout(() => {
    isScrollLocked.value = false;
  }, 600); // 600ms - время для плавной анимации скролла
};

// функция успешной загрузки элемента
const onItemLoad = (type: string, index: number) => {
  if (!loadedItems.value.includes(index)) {
    loadedItems.value.push(index);
  }

  // Если загруженный элемент - текущий ожидаемый, переходим к следующему
  if (index === currentLoadingIndex.value) {
    currentLoadingIndex.value++;
  }
}

onMounted(() => {
  document.body.style.overflow = 'hidden';

  // Добавляем состояние в историю для возможности отмены перехода назад
  history.pushState({ gallery: true }, '');

  // Добавляем обработчик для кнопки "назад" браузера
  window.addEventListener('popstate', handlePopState);

  // Если указан индекс активного слайда, загружаем все элементы до него включительно
  // Это позволит быстро показать нужное изображение и сделать к нему скролл
  if (indexActiveSlide.value > 0) {
    currentLoadingIndex.value = indexActiveSlide.value;
    // Блокируем скролл до тех пор, пока не выполнится автоматический скролл
    isScrollLocked.value = true;
    const minLoadingTime = 500 + (indexActiveSlide.value * 150);
    nextTick(() => {
      setTimeout(() => {
        scrollToItem(indexActiveSlide.value);
      }, minLoadingTime);
    });

    // Аварийная разблокировка через 10 секунд на случай, если что-то пойдет не так
    setTimeout(() => {
      if (isScrollLocked.value) {
        console.warn('Аварийная разблокировка скролла через 10 секунд');
        isScrollLocked.value = false;
      }
    }, 10000);
  }
});
onBeforeUnmount(() => {
  console.log('GalleryMobileComponent: beforeUnmount, removing popstate handler');
  document.body.style.overflow = '';

  // Удаляем обработчик события popstate
  window.removeEventListener('popstate', handlePopState);
});
</script>

<style lang="scss">
.gallery-mobile-component {
  position: fixed;
  z-index: 999;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: white;
}
.gallery-mobile-component__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 40px;
  position: absolute;
  z-index: 10;
  top: 0; left: 0; right: 0;
  background: white;
  backdrop-filter: blur(5px);
  box-sizing: border-box;
}
.gallery-mobile-component__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  z-index: 10;
}
.gallery-mobile-component__images {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  padding-top: 50px;
  box-sizing: border-box;
  overflow: auto;
  height: 100%;

  &.scroll-locked {
    overflow: hidden !important;
    pointer-events: none;
  }

  .collection-slider-images__slide-hint {
    top: 4px; left: 4px;
    padding: 8px 12px;
    border-radius: 4px;
  }
}

.gallery-mobile-component__loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.gallery-mobile-component__loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  p {
    margin: 0;
    color: #333;
    font-size: 14px;
    font-weight: 500;
  }
}

.gallery-mobile-component__placeholder {
  width: 100%;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 4px;
}

.gallery-mobile-component__loader {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top-color: #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
